import { useMemo, useState } from 'react';
import { ArrowRight, BadgeEuro, Boxes, PackageCheck, Tags } from 'lucide-react';
import { categoryLabel, commercialModeLabel, formatEuro, liveDropCategoryOptions, liveDropProducts, matchConfidenceLabel } from '../data/liveDrop';
import { getProductLaunchContent } from '../data/productContent';
import { trackEvent } from '../lib/analytics';

export function LiveDropSection() {
  const [activeCategory, setActiveCategory] = useState('alles');
  const visibleProducts = useMemo(() => {
    return activeCategory === 'alles' ? liveDropProducts : liveDropProducts.filter((product) => product.category === activeCategory);
  }, [activeCategory]);

  function setCategoryFilter(category: string) {
    setActiveCategory(category);
    trackEvent({ event: 'category_filter', location: 'live_drop', label: category, category, routeFamily: 'home', placement: 'live_drop_filters' });
  }

  return (
    <section className="section liveDropSection" id="live-drop">
      <div className="liveDropHeader">
        <div>
          <p className="eyebrow">Nieuw in de shop</p>
          <h2>De eerste pickleball collectie voor thuis, training en club.</h2>
          <p className="sectionIntro">
            Bekijk startersets, paddles, ballen, netten en accessoires die als eerste live gaan. Bestellen opent zodra prijs, levering en productinformatie definitief zijn.
          </p>
        </div>
        <div className="dropStats shopStats" aria-label="Shop samenvatting">
          <article><Boxes /><strong>{liveDropProducts.length}</strong><span>producten in de eerste selectie</span></article>
          <article><Tags /><strong>9</strong><span>categorieen voor spelers en clubs</span></article>
          <article><PackageCheck /><strong>NL/BE</strong><span>service, retouren en levering voorbereid</span></article>
        </div>
      </div>

      <div className="liveDropControls" aria-label="Filter collectie">
        {liveDropCategoryOptions.map((option) => (
          <button
            aria-pressed={activeCategory === option.key}
            className={activeCategory === option.key ? 'active' : ''}
            key={option.key}
            onClick={() => setCategoryFilter(option.key)}
            type="button"
          >
            {option.label}
            <span>{option.count}</span>
          </button>
        ))}
      </div>

      <div className="liveDropGrid">
        {visibleProducts.map((product) => {
          const content = getProductLaunchContent(product.sku);
          return (
            <a
              className="liveDropCard"
              href={product.pdpPath}
              key={product.sku}
              onClick={() => trackEvent({ event: 'select_item', location: 'live_drop', label: product.name, sku: product.sku, category: product.category, brand: product.brand, matchType: product.marketResearch.benchmark.matchType, placement: 'live_drop_card', routeFamily: 'home' })}
            >
              <div className="productVisual productVisualImage shopCardVisual">
                <img src={content.image} alt="" loading="lazy" />
                <span>Eerste batch volgt</span>
              </div>
              <div className="liveDropCardBody">
                <div className="productMeta">
                  <span>{categoryLabel(product.category)}</span>
                  <span>{product.brand}</span>
                </div>
                <h3>{product.name}</h3>
                <p className="shopCardCopy">{content.headline}</p>
                <dl className="shopCardSpecs">
                  <div><dt>Niveau</dt><dd>{product.level}</dd></div>
                  <div><dt>Voor</dt><dd>{product.audience}</dd></div>
                </dl>
                <div className="priceBench shopPricePanel">
                  <span><BadgeEuro size={16} /> Richtprijs incl. btw</span>
                  <strong>{formatEuro(product.marketResearch.targetPriceInclVat)}</strong>
                  <small>{matchConfidenceLabel(product.marketResearch.benchmark.matchType)}; definitieve prijs volgt bij livegang</small>
                </div>
                <div className="cardFooter shopCardActions">
                  <span>{commercialModeLabel(product.commercialMode)}</span>
                  <strong>Bekijk product <ArrowRight size={16} /></strong>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
